import FormTextField from '../../../../../../ui/forms/fields/text-field';
import React, { ChangeEvent, ClipboardEvent, KeyboardEvent, FocusEvent, useCallback } from 'react';
import store, {
    billingAddressActions,
    rootActions,
    shippingAddressActions,
    useAppDispatch,
    useAppSelector
} from '../../../data/store';
import { removeInvisibleCharsOnPaste, updateControl, validateControl } from '../../../../../../domain/validation/validation';
import { IFormControl } from '../../../../../../ui/forms/interfaces';
import CountryAndStateFields from '../../../../../../ui/forms/fields/country-and-state-fields';
import FormPhoneField from '../../../../../../ui/forms/fields/phone/phone-field';
import { getTax } from '../../../data/data-service';
import { getCartCountries, getLocalData, getTaxAddressData } from '../../../domain/shopping-cart-address-provider';
import TaxID from '../taxid/taxid';
import { USA_ID } from '../../../../../../domain/addresses/addresses-provider';
import { TaxIdStatusEnum } from '../../../enums/tax-id-status-enum';
import { debounce } from 'lodash';
import { FIELD_DEBOUNCE_DURATION } from '../../../domain/shopping-cart-provider';

const BillingForm = () => {

    const dispatch = useAppDispatch();
    const cartData = useAppSelector(store => store.root.cartData);
    const countries = useAppSelector(store => store.root.countries);

    // form controls
    const billToControl = useAppSelector(store => store.billingAddress.billToControl);
    const billingAddressLine1Control = useAppSelector(store => store.billingAddress.addressLine1Control);
    const billingAddressLine2Control = useAppSelector(store => store.billingAddress.addressLine2Control);
    const cityControl = useAppSelector(store => store.billingAddress.cityControl);
    const zipCodeControl = useAppSelector(store => store.billingAddress.zipCodeControl);
    const countryIdControl = useAppSelector(store => store.billingAddress.countryIdControl);
    const stateIdControl = useAppSelector(store => store.billingAddress.stateIdControl);
    const phoneControl = useAppSelector(store => store.billingAddress.phoneControl);
    const phoneCountryIdControl = useAppSelector(store => store.billingAddress.phoneCountryIdControl);

    const isAmerica = countryIdControl.value.toString() === USA_ID.toString();

    const taxIdControl = useAppSelector(store => store.billingAddress.taxIdControl);
    const taxIdSelectedStatusControl = useAppSelector(store => store.billingAddress.taxIdSelectedStatusControl);
    const taxID = taxIdSelectedStatusControl.value === TaxIdStatusEnum.YES ? taxIdControl.value : '';

    const upsShippingCodeControl = useAppSelector(store => store.shippingAddress.upsShippingCodeControl);

    /**
     * On input change
     */
    const handleChangeEvent = (evt: ChangeEvent, control: IFormControl, controlName: string) => {
        const $target = evt.target as HTMLInputElement;

        dispatch(
            billingAddressActions.setControl({
                controlName,
                control: updateControl(control, $target.value, !control.isValid)
            })
        );
    };

    // debounce is an external dependency, and it can't be replaced with inline function
    // eslint-disable-next-line
    const debounced = useCallback(
        debounce(
            (evt: ChangeEvent, controlName: string) => onTaxChange(evt, controlName),
            FIELD_DEBOUNCE_DURATION
        ),
        []
    );

    /**
     * on country, state, or zipcode change ---> tax can be changed,
     * in such case ----> update the cart data
     */
    const onTaxChange = async (evt: ChangeEvent, controlName: string) => {

        const state = store.getState();
        const _countries = state.root.countries;
        const _billingCountryIdControl = state.billingAddress.countryIdControl;
        const _billingStateIdControl = state.billingAddress.stateIdControl;
        const _billingZipCodeControl = state.billingAddress.zipCodeControl;

        const $target = evt.target as HTMLInputElement;
        let taxAddressData = null;

        switch(controlName){
            case 'countryIdControl':{
                const countryId = Number($target.value) || 0;
                taxAddressData = getTaxAddressData(_countries, countryId, _billingStateIdControl.value, _billingZipCodeControl.value);
                break;
            }
            case 'stateIdControl':{
                const stateId = Number($target.value) || 0;
                taxAddressData = getTaxAddressData(_countries, _billingCountryIdControl.value, stateId, _billingZipCodeControl.value);
                break;
            }
            case 'zipCodeControl':{
                const zipCode = $target.value;
                taxAddressData = getTaxAddressData(_countries, _billingCountryIdControl.value, _billingStateIdControl.value, zipCode);
                break;
            }
        }

        if(!taxAddressData) return;

        const localData = getLocalData();
        localData.taxCountry2LetterCode = taxAddressData.taxCountry2LetterCode;
        localData.taxState2LetterCode = taxAddressData.taxState2LetterCode;
        localData.taxZipCode = taxAddressData.taxZipCode;
        localData.taxID = taxID;

        const response = await getTax(localData);

        if(!response || response.statusCode !== 200 || !response.data.isValid || !response.data.relatedData) return;

        dispatch(
            rootActions.setCartData(response.data.relatedData)
        );
    }

    /**
     * on country / state changes --> shipping options should be reset
     */
    const resetShipping = () => {
        dispatch(
            shippingAddressActions.main({
                upsShippingOptions: [],
                upsShippingError: '',
            })
        );

        dispatch(
            shippingAddressActions.setControl({
                controlName: 'upsShippingCodeControl',
                control: updateControl(upsShippingCodeControl, '', true)
            })
        );
    }

    /**
     * handle key up event
     */
    const handleKeyUp = (evt: KeyboardEvent, control: IFormControl, controlName: string) => {
        if(evt.key === 'Escape') {
            dispatch(
                billingAddressActions.setControl({
                    controlName,
                    control: updateControl(control, '', !control.isValid)
                })
            );
        }
    };

    /**
     * validate on blur
     */
    const handleBlurEvent = (evt: FocusEvent, control: IFormControl, controlName: string) => {
        dispatch(
            billingAddressActions.setControl({
                controlName,
                control: validateControl(control),
            })
        );
    };

    /**
     * remove invisible characters on paste event
     */
    const handlePasteEvent = (evt: ClipboardEvent<HTMLInputElement>, control: IFormControl, controlName: string) => {

        dispatch(
            billingAddressActions.setControl({
                controlName,
                control: updateControl(control,  removeInvisibleCharsOnPaste(evt), !control.isValid)
            })
        );
    };

    return (
        <>
            <FormTextField
                id={ 'billTo' }
                label={ 'Bill To Name' }
                control={ billToControl }
                qa={ 'cart-step3-bill-name-tb' }
                maxLength={ 200 }
                textBoxClasses="rounded"
                onChange={ evt => {
                    handleChangeEvent(evt, billToControl, 'billToControl');
                }}
                onBlur={ evt => {
                    handleBlurEvent(evt, billToControl, 'billToControl');
                }}
                onKeyUp={ evt => {
                    handleKeyUp(evt, billToControl, 'billToControl');
                }}
                onPaste={ (evt: ClipboardEvent<HTMLInputElement>) => {
                    handlePasteEvent(evt, billToControl, 'billToControl');
                }}
            />

            <FormTextField
                id={ 'billingAddressLine1' }
                label={ 'Address Line 1' }
                control={ billingAddressLine1Control }
                qa={ 'cart-step3-bill-addr1-tb' }
                maxLength={ 100 }
                textBoxClasses="rounded"
                onChange={ evt => {
                    handleChangeEvent(evt, billingAddressLine1Control, 'addressLine1Control');
                }}
                onBlur={ evt => {
                    handleBlurEvent(evt, billingAddressLine1Control, 'addressLine1Control');
                }}
                onKeyUp={ evt => {
                    handleKeyUp(evt, billingAddressLine1Control, 'addressLine1Control');
                }}
                onPaste={ (evt: ClipboardEvent<HTMLInputElement>) => {
                    handlePasteEvent(evt, billingAddressLine1Control, 'addressLine1Control');
                }}
            />

            <FormTextField
                id={ 'billingAddressLine2' }
                label={ 'Address Line 2 (Optional)' }
                control={ billingAddressLine2Control }
                maxLength={ 100 }
                textBoxClasses="rounded"
                onChange={ evt => {
                    handleChangeEvent(evt, billingAddressLine2Control, 'addressLine2Control');
                }}
                onBlur={ evt => {
                    handleBlurEvent(evt, billingAddressLine2Control, 'addressLine2Control');
                }}
                onKeyUp={ evt => {
                    handleKeyUp(evt, billingAddressLine2Control, 'addressLine2Control');
                }}
                onPaste={ (evt: ClipboardEvent<HTMLInputElement>) => {
                    handlePasteEvent(evt, billingAddressLine2Control, 'addressLine2Control');
                }}
            />

            <FormTextField
                id={ 'billingCity' }
                label={ 'City' }
                control={ cityControl }
                maxLength={ 100 }
                qa={ 'cart-step3-bill-city-tb' }
                textBoxClasses="rounded"
                onChange={ evt => {
                    handleChangeEvent(evt, cityControl, 'cityControl');
                    resetShipping();
                }}
                onBlur={ evt => {
                    handleBlurEvent(evt, cityControl, 'cityControl');
                }}
                onKeyUp={ evt => {
                    handleKeyUp(evt, cityControl, 'cityControl');
                }}
                onPaste={ (evt: ClipboardEvent<HTMLInputElement>) => {
                    handlePasteEvent(evt, cityControl, 'cityControl');
                }}
            />

            <CountryAndStateFields
                idPrefix="billing"
                countries={ getCartCountries(countries, cartData) }
                countryIdControl={ countryIdControl }
                stateIdControl={ stateIdControl }
                qaCountry={ 'cart-step3-bill-ctry-drop' }
                qaState={ 'cart-step3-bill-state-drop' }
                onBlur={ () => {} }
                onCountryChange={ async evt => {
                    handleChangeEvent(evt, countryIdControl, 'countryIdControl');
                    resetShipping();
                    await onTaxChange(evt, 'countryIdControl');
                }}
                onStateChange={ async evt => {
                    const $target = evt.target as HTMLInputElement;
                    let control = updateControl(stateIdControl, $target.value, !stateIdControl.isValid);
                    control = validateControl(control);

                    dispatch(
                        billingAddressActions.setControl({
                            controlName: 'stateIdControl',
                            control,
                        })
                    );

                    resetShipping();

                    await onTaxChange(evt, 'stateIdControl');
                }}
                onClick={ () => {} }
            />

            <FormTextField
                id={ 'billingZipCode' }
                label={ 'Zip Code' }
                control={ zipCodeControl }
                maxLength={ 10 }
                qa={ 'cart-step3-bill-zip-tb' }
                textBoxClasses="rounded"
                onChange={ evt => {
                    handleChangeEvent(evt, zipCodeControl, 'zipCodeControl');
                    resetShipping();
                    debounced(evt, 'zipCodeControl');
                }}
                onBlur={ async evt => {
                    handleBlurEvent(evt, zipCodeControl, 'zipCodeControl');
                    await onTaxChange(evt, 'zipCodeControl');
                }}
                onKeyUp={ evt => {
                    handleKeyUp(evt, zipCodeControl, 'zipCodeControl');
                }}
                onPaste={ (evt: ClipboardEvent<HTMLInputElement>) => {
                    handlePasteEvent(evt, zipCodeControl, 'zipCodeControl');
                }}
            />

            <FormPhoneField
                id={ 'billingPhone' }
                label={ 'Phone (Digits only)' }
                qa={ 'cart-step3-bill-phone-tb' }
                countries={ countries }
                phoneControl={ phoneControl }
                phoneCountryIdControl={ phoneCountryIdControl }
                textBoxClasses="rounded"
                onPhoneChange={ evt => {
                    handleChangeEvent(evt, phoneControl, 'phoneControl');
                }}
                onCountryIdChange={ (countryId: number) => {
                    dispatch(
                        billingAddressActions.setControl({
                            controlName: 'phoneCountryIdControl',
                            control: updateControl(phoneCountryIdControl, countryId, !phoneCountryIdControl.isValid)
                        })
                    );
                }}
            />

            { !isAmerica && <TaxID /> }
        </>
    )
};

export default BillingForm;